import Button from 'tailwind-components/Button/Button';
import HiddenSideSection from 'tailwind-components/HiddenSideSection/HiddenSideSection';
import Modal from 'tailwind-components/Modal/Modal';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import SideSection from 'tailwind-components/SideSection/SideSection';
import SideSectionUnappointed from 'tailwind-components/SideSection/SideSectionUnnapointed';
import genericStyle from '../../DashboardCustomer.module.scss';
import style from './TakeActionModal.module.scss';
import { getStateNameFromStateCode } from 'helpers/states';
import { useDispatch } from 'react-redux';
import { carrierInProgress, ignoreCarrier } from 'state/actions/healthSherpa';
import { showAlertModal } from 'state/actions/site';
import { isAgentAppointedWithCarrier } from 'helpers/appointments/appointments';

export default function TakeActionModal({
  activeCarrier,
  handleCloseModal,
  createAppointmentRequest,
  user,
  setLocalLoader,
  applyFilters,
  activePolicyData = {}
}) {
  const dispatch = useDispatch();
  const sideSectionRef = useRef();
  const sideSectionUnAppRef = useRef();

  const isCarrierIgnored = activeCarrier.ignored;

  const [currentStep, setCurrentStep] = useState(0);
  const [appointmentRequestStatus, setAppointmentRequestStatus] = useState({
    appointments: [],
    loading: false,
    disabled: true // disabled by default, will be enabled when appointments are added
  });

  const putCarrierInProgress = useCallback((id, appointments) => {
    setLocalLoader(true);
    dispatch(
      carrierInProgress(
        function loader(val) {
          setLocalLoader(val);
          applyFilters();
          handleCloseModal();
        },
        () => {},
        id,
        appointments
      )
    );
  }, []);

  const STATE_NAME_IN_POLICY = getStateNameFromStateCode(
    activePolicyData.State
  );

  if (!STATE_NAME_IN_POLICY) {
    handleCloseModal();
    dispatch(
      showAlertModal({
        title: 'Invalid Policy State',
        message:
          'Policy state is not valid or not found. Please check the policy details or contact support.',
        buttonText: 'Dismiss'
      })
    );
    return null;
  }

  function handleIgnoreButtonClick() {
    if (currentStep === 0 && !isCarrierIgnored) {
      setCurrentStep(1);
      return;
    }

    if (!activeCarrier.id) return;

    dispatch(
      ignoreCarrier(
        function loader(val) {
          setLocalLoader(val);
          applyFilters();
          handleCloseModal();
        },
        () => {},
        activeCarrier.id,
        {
          ignore_appt: true
        },
        isCarrierIgnored
      )
    );
  }

  const isAppointed =
    isAgentAppointedWithCarrier(activeCarrier.name, user) ||
    activeCarrier.pending === true ||
    (Array.isArray(activeCarrier.pending) && activeCarrier.pending.length > 0);

  const isStateOfferedByCarrier =
    activeCarrier.statesOffered.includes(STATE_NAME_IN_POLICY);

  const confirmedAppointments = activeCarrier.confirmed;
  const pendingAppointments = activeCarrier.pending;

  const agentHasRequestedPolicyState =
    confirmedAppointments.includes(STATE_NAME_IN_POLICY) ||
    pendingAppointments.includes(STATE_NAME_IN_POLICY);

  const isAgentInPolicyViewingAgent =
    String(activePolicyData.AssistorNpn) === String(user.NPN);

  function getModalTitle() {
    if (currentStep === 1) {
      return 'WARNING';
    }
    if (isCarrierIgnored) {
      return `Un-Ignore Carrier - ${activeCarrier.name}`;
    }
    if (!isAgentInPolicyViewingAgent) {
      return `Agent Not Appointed - ${activeCarrier.name}`;
    }
    if (agentHasRequestedPolicyState) {
      return `Agent Already Appointed - ${activeCarrier.name}`;
    }
    if (!isStateOfferedByCarrier) {
      return `State Not Offered - ${activeCarrier.name}`;
    }
    return `Request Appointments for ${activeCarrier.name}`;
  }

  return (
    <Modal
      danger={currentStep === 1}
      active={activeCarrier.id}
      title={getModalTitle()}
      showScrollbar
      footer={
        <div
          className={genericStyle.wrapButtonsSmall}
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '15px',
            justifyContent: 'space-between'
          }}
        >
          <Button
            fill
            variant='secondary'
            text='Cancel'
            onClick={
              currentStep === 0 ? handleCloseModal : () => setCurrentStep(0) // if
            }
          />
          <Button
            fill
            className={genericStyle.buttonNoWrap}
            variant={isCarrierIgnored ? 'primary' : 'danger'}
            text={isCarrierIgnored ? 'Un-Ignore Carrier' : 'Ignore Carrier'}
            onClick={handleIgnoreButtonClick}
          />
          {currentStep === 0 &&
            !isCarrierIgnored &&
            isAgentInPolicyViewingAgent &&
            !agentHasRequestedPolicyState &&
            isStateOfferedByCarrier && (
              <Button
                fill
                className={genericStyle.buttonNoWrap}
                variant='primaryDarkened'
                text='Send Request'
                isDisabled={appointmentRequestStatus.disabled}
                isLoading={appointmentRequestStatus.loading}
                onClick={() =>
                  isAppointed
                    ? sideSectionRef.current.handleRequestClick()
                    : sideSectionUnAppRef.current.handleRequestClick()
                }
              />
            )}
        </div>
      }
      closeModal={handleCloseModal}
    >
      <div
        style={{
          borderTop: '2px solid #f6f6f6',
          marginBlock: '10px'
        }}
      />
      {currentStep === 0 ? (
        isCarrierIgnored ? (
          <p className={style.informationText}>
            <strong>You have ignored this carrier.</strong> If you would like to
            request appointments for this carrier, please{' '}
            <strong>un-ignore it by clicking the button below</strong>.
          </p>
        ) : isAgentInPolicyViewingAgent ? (
          agentHasRequestedPolicyState ? (
            <p className={style.informationText}>
              On the effective date of the appointment,{' '}
              <strong>you were not appointed</strong> for{' '}
              <strong>{STATE_NAME_IN_POLICY}</strong> under{' '}
              <strong>{activeCarrier.name}</strong>.
            </p>
          ) : isStateOfferedByCarrier ? (
            !activeCarrier.name ? (
              <HiddenSideSection text='Click on any pending carriers (in yellow color) to view more information on your appointment status and progress notes for that carrier.' />
            ) : isAppointed ? ( // agent is appointed or has pending RA tickets
              <SideSection
                {...activeCarrier}
                ref={sideSectionRef}
                createAppointmentRequest={createAppointmentRequest}
                obFormCompleted={user.Initial_Onboarding_Form_Completed}
                simplified
                setApptRequestStatus={setAppointmentRequestStatus}
                carrierInProgress={putCarrierInProgress}
              />
            ) : (
              <SideSectionUnappointed
                {...activeCarrier}
                ref={sideSectionUnAppRef}
                createAppointmentRequest={createAppointmentRequest}
                obFormCompleted={user.Initial_Onboarding_Form_Completed}
                simplified
                setApptRequestStatus={setAppointmentRequestStatus}
                carrierInProgress={putCarrierInProgress}
              />
            )
          ) : (
            <p className={style.informationText}>
              <strong>{STATE_NAME_IN_POLICY}</strong> is currently not offered
              by <strong>{activeCarrier.name}</strong>.
            </p>
          )
        ) : (
          <p className={style.informationText}>
            The{' '}
            <strong>
              agent was not appointed for {STATE_NAME_IN_POLICY} as of the
              effective date
            </strong>
            . Please advise the agent to request the appropriate state
            appointment, if they have not already done so, to{' '}
            <strong>avoid any issues with commission payments</strong>.
          </p>
        )
      ) : (
        <div>
          <h2
            style={{
              color: 'red'
            }}
          >
            Be reminded that this option should only be selected if you are
            appointed with the carrier outside of OM, as you will not receive
            commissions for unappointed carriers.
          </h2>
        </div>
      )}
    </Modal>
  );
}

TakeActionModal.propTypes = {
  activeCarrier: PropTypes.object,
  handleCloseModal: PropTypes.func,
  createAppointmentRequest: PropTypes.func,
  user: PropTypes.object,
  setLocalLoader: PropTypes.func,
  applyFilters: PropTypes.func,
  activePolicyData: PropTypes.object
};
